if (!customElements.get('fc-product-page')) {
  customElements.define('fc-product-page', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.addEventListener('click', event => {
        const link = event.target.closest('[data-gallery-link]');
        if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const media = this.querySelector(link.getAttribute('href'));
        const track = this.querySelector('.fc-product__media-track');
        if (!media || !track) return;
        event.preventDefault();
        track.scrollBy({left: media.getBoundingClientRect().left - track.getBoundingClientRect().left,
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
      }, options);
      this.addEventListener('click', event => {
        const option = event.target.closest('[data-option-id]');
        if (!option || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        if (!this.adding) this.selectOption(option);
      }, options);
      const track = this.querySelector('.fc-product__media-track');
      if (track) {
        this.galleryObserver = new IntersectionObserver(entries => {
          for (const entry of entries) {
            if (!entry.isIntersecting || entry.intersectionRatio < .6) {
              entry.target.querySelectorAll('video').forEach(video => video.pause());
              continue;
            }
            this.querySelectorAll('[data-gallery-link]').forEach(link => {
              if (link.hash === '#' + entry.target.id) link.setAttribute('aria-current', 'true');
              else link.removeAttribute('aria-current');
            });
          }
        }, {root: track, threshold: .6});
        track.querySelectorAll('.fc-product__media').forEach(media => this.galleryObserver.observe(media));
        track.addEventListener('keydown', event => {
          if (event.target !== track || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
          event.preventDefault();
          const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
          if (event.key === 'Home' || event.key === 'End') track.scrollTo({left: event.key === 'Home' ? 0 : track.scrollWidth, behavior});
          else track.scrollBy({left: (event.key === 'ArrowRight' ? 1 : -1) * track.clientWidth, behavior});
        }, options);
      }
      this.querySelector('.fc-product__form')?.addEventListener('submit', event => {
        // Native accelerated checkout retains its own Shopify behavior.
        if (event.submitter && event.submitter.name !== 'add') return;
        event.preventDefault();
        this.addProduct(event.target);
      }, options);
    }
    async selectOption(option) {
      this.variantRequest?.abort();
      const request = new AbortController();
      this.variantRequest = request;
      const form = this.querySelector('.fc-product__form');
      const button = form.querySelector('[name="add"]');
      const status = form.querySelector('.fc-product__status');
      option.closest('fieldset').querySelectorAll('[data-option-id]').forEach(link => {
        if (link === option) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
      const url = new URL(option.href);
      url.hash = '';
      url.searchParams.delete('variant');
      url.searchParams.set('option_values', Array.from(this.querySelectorAll('[data-option-id][aria-current]'), link => link.dataset.optionId).join(','));
      url.searchParams.set('section_id', this.dataset.sectionId);
      this.selecting = true;
      button.disabled = true;
      form.setAttribute('aria-busy', 'true');
      const checkout = form.querySelector('.fc-product__checkout');
      if (checkout) checkout.inert = true;
      status.textContent = '';
      status.removeAttribute('data-error');
      try {
        const response = await fetch(url, {signal: request.signal});
        if (!response.ok) throw new Error('Variant request failed');
        const html = await response.text();
        if (request.signal.aborted) return;
        const next = new DOMParser().parseFromString(html, 'text/html').querySelector('fc-product-page');
        const nextForm = next?.querySelector('.fc-product__form');
        if (!nextForm) throw new Error('Missing product response');
        // Retain the page, gallery, focused option, quantity input and form node.
        this.querySelector('.fc-product__price').innerHTML = next.querySelector('.fc-product__price').innerHTML;
        this.querySelectorAll('[data-option-id]').forEach(link => {
          const updated = Array.from(next.querySelectorAll('[data-option-id]')).find(item => item.dataset.optionId === link.dataset.optionId);
          if (!updated) return;
          link.href = updated.href;
          link.className = updated.className;
          link.innerHTML = updated.innerHTML;
          if (updated.hasAttribute('aria-current')) link.setAttribute('aria-current', 'true');
          else link.removeAttribute('aria-current');
        });
        const id = form.querySelector('[name="id"]');
        id.value = nextForm.querySelector('[name="id"]').value;
        id.dispatchEvent(new Event('change', {bubbles:true}));
        const quantity = form.querySelector('[name="quantity"]');
        const nextQuantity = nextForm.querySelector('[name="quantity"]');
        for (const attribute of ['min','max','step']) {
          if (nextQuantity.hasAttribute(attribute)) quantity.setAttribute(attribute, nextQuantity.getAttribute(attribute));
          else quantity.removeAttribute(attribute);
        }
        const min = Number(quantity.min) || 1, step = Number(quantity.step) || 1;
        let count = Math.max(min, min + Math.ceil((Number(quantity.value) - min) / step) * step);
        if (quantity.max) count = Math.min(count, Number(quantity.max));
        quantity.value = Number.isFinite(count) ? count : min;
        const plan = form.querySelector('[name="selling_plan"]');
        const nextPlan = nextForm.querySelector('[name="selling_plan"]');
        if (plan && nextPlan) {
          const selectedPlan = plan.value;
          plan.innerHTML = nextPlan.innerHTML;
          if (Array.from(plan.options).some(item => item.value === selectedPlan)) plan.value = selectedPlan;
        }
        const nextButton = nextForm.querySelector('[name="add"]');
        button.disabled = nextButton.disabled;
        button.textContent = nextButton.textContent;
        const nextCheckout = nextForm.querySelector('.fc-product__checkout');
        if (checkout && nextCheckout) checkout.replaceWith(nextCheckout);
        else if (checkout) {
          // Keep the existing payment area in the layout when a size sells out.
          checkout.style.visibility = 'hidden';
          checkout.inert = true;
        }
        else if (nextCheckout) button.after(nextCheckout);
        window.Shopify?.PaymentButton?.init?.();
        url.searchParams.delete('section_id');
        if (id.value) {
          url.searchParams.delete('option_values');
          url.searchParams.set('variant', id.value);
        }
        history.replaceState(history.state, '', url);
      } catch (error) {
        if (request.signal.aborted) return;
        // Never allow an old variant to be purchased after a failed selection.
        form.querySelector('[name="id"]').value = '';
        status.setAttribute('data-error', '');
        status.textContent = 'Unable to load this size. Select a size again to retry.';
      } finally {
        if (this.variantRequest === request) {
          this.selecting = false;
          form.removeAttribute('aria-busy');
        }
      }
    }
    async addProduct(form) {
      if (this.selecting || this.adding || !form.reportValidity()) return;
      const button = form.querySelector('[name="add"]');
      if (button.disabled) return;
      const status = form.querySelector('.fc-product__status');
      const payload = new FormData(form);
      this.adding = true;
      button.disabled = true;
      button.setAttribute('aria-busy','true');
      status.removeAttribute('data-error');
      status.textContent = 'Adding to cart…';
      try {
        const response = await fetch(this.dataset.cartAddUrl, {method:'POST',body:payload,headers:{Accept:'application/json'}});
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Unable to add this item. Please try again.');
        status.textContent = 'Added to cart. Opening your cart…';
        window.location.assign(this.dataset.cartUrl);
      } catch (error) {
        status.setAttribute('data-error','');
        status.textContent = error instanceof TypeError ? 'Unable to confirm the cart update. Check your connection and cart before retrying.' : error.message;
      } finally {
        this.adding = false;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
    disconnectedCallback() {
      this.variantRequest?.abort();
      this.events?.abort();
      this.events = null;
      this.galleryObserver?.disconnect();
    }
  });
}
